import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Select, Spin, Typography, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { OrderRoomPageListPreset } from '../../../../service/storeRevenueApi';
import { useOrderRoomPresetData } from '../../hooks/useOrderRoomPresetData';

function rowGuest(r: Record<string, unknown>): string {
  const s = r.guestName ?? r.name ?? r.krName ?? r.reservedName ?? '';
  return String(s || '—');
}

function rowRoomNo(r: Record<string, unknown>): string {
  const s = r.roomNumber ?? r.roomNo ?? r.roomNumberStr ?? '';
  return String(s || '—');
}

function rowRoomType(r: Record<string, unknown>): string {
  const s = r.aliasName ?? r.roomTypeName ?? r.alias ?? '';
  return String(s || '—');
}

/** 与 msCentral storeRevenueProxyService.roomStatusOfRow 一致；5 = 已退房 */
function roomStatusOfRow(r: Record<string, unknown>): number {
  const n = Number(r.roomStatus ?? r.room_status ?? -999);
  return Number.isFinite(n) ? n : -999;
}

/** Axios 风格错误（避免强依赖 axios 类型） */
function formatOrderRoomPresetError(err: unknown): string {
  if (!(err instanceof Error)) return String(err);
  const ax = err as Error & { response?: { status?: number; data?: { message?: string } } };
  const status = ax.response?.status;
  const backendMsg = ax.response?.data?.message;
  if (status === 404) {
    return '接口不存在（HTTP 404）：后端未提供 GET /api/store-revenue/proxy/order-room-page-list。请将 msCentral 更新到含该路由的版本并重启服务（本地开发一般为 50045 端口）。';
  }
  if (status === 503 && backendMsg) return backendMsg;
  if (status === 503) {
    return '服务暂不可用（503）：请检查门店营收 Token 配置或稍后重试。';
  }
  return err.message;
}

const PRESET_META: Record<OrderRoomPageListPreset, { title: string; dataId: string }> = {
  'expected-arrival-today': {
    title: '预抵',
    dataId: 'card:order-room-expected-arrival',
  },
  'expected-departure-today': {
    title: '预离',
    dataId: 'card:order-room-expected-departure',
  },
  'in-house-today': {
    title: '在住',
    dataId: 'card:order-room-in-house',
  },
};

export interface OrderRoomPresetCardProps {
  storeId: number;
  preset: OrderRoomPageListPreset;
  style?: React.CSSProperties;
  /** 拖拽排序时由 SortableComponentSlot 注入，显示在标题左侧 */
  titleLeftAdornment?: React.ReactNode;
  /** 对外暴露当前加载态，便于截图页同步 body[data-screenshot-*] 标记 */
  onLoadingStateChange?: (state: { loading: boolean; hasError: boolean; hasData: boolean }) => void;
}

const ALL_ROOM_TYPES = '';

/** 预离：按是否已退房（房单 roomStatus 5）筛选 */
type DepartureCheckoutFilter = 'all' | 'checked-out' | 'not-checked-out';
const ALL_DEPARTURE_CHECKOUT: DepartureCheckoutFilter = 'all';

/** 预抵：房单 roomStatus 0=未入住 1=已入住（与 FSM 预设一致） */
type ArrivalCheckInFilter = 'all' | 'checked-in' | 'not-checked-in';
const ALL_ARRIVAL_CHECKIN: ArrivalCheckInFilter = 'all';

const OrderRoomPresetCard: React.FC<OrderRoomPresetCardProps> = ({
  storeId,
  preset,
  style,
  titleLeftAdornment,
  onLoadingStateChange,
}) => {
  const { data, loading, error } = useOrderRoomPresetData(storeId, preset);
  const meta = PRESET_META[preset];
  const [roomTypeFilter, setRoomTypeFilter] = useState<string>(ALL_ROOM_TYPES);
  const [departureCheckoutFilter, setDepartureCheckoutFilter] =
    useState<DepartureCheckoutFilter>(ALL_DEPARTURE_CHECKOUT);
  const [arrivalCheckInFilter, setArrivalCheckInFilter] =
    useState<ArrivalCheckInFilter>(ALL_ARRIVAL_CHECKIN);

  const tableWrapRef = useRef<HTMLDivElement>(null);
  const [tableScrollY, setTableScrollY] = useState(200);

  const allRows = useMemo(() => (Array.isArray(data?.list) ? data.list : []), [data]);

  useEffect(() => {
    onLoadingStateChange?.({
      loading,
      hasError: Boolean(error),
      hasData: Array.isArray(data?.list) && data.list.length > 0,
    });
  }, [loading, error, data, onLoadingStateChange]);

  const roomTypeOptions = useMemo(() => {
    const uniq = new Set<string>();
    for (const r of allRows) {
      const name = rowRoomType(r);
      if (name && name !== '—') uniq.add(name);
    }
    return Array.from(uniq).sort((a, b) => a.localeCompare(b, 'zh-CN'));
  }, [allRows]);

  useEffect(() => {
    setRoomTypeFilter(ALL_ROOM_TYPES);
    setDepartureCheckoutFilter(ALL_DEPARTURE_CHECKOUT);
    setArrivalCheckInFilter(ALL_ARRIVAL_CHECKIN);
  }, [storeId, preset, data?.dateLabel]);

  useEffect(() => {
    if (
      roomTypeFilter &&
      roomTypeOptions.length > 0 &&
      !roomTypeOptions.includes(roomTypeFilter)
    ) {
      setRoomTypeFilter(ALL_ROOM_TYPES);
    }
  }, [roomTypeOptions, roomTypeFilter]);

  useLayoutEffect(() => {
    const el = tableWrapRef.current;
    if (!el) return;
    const update = () => {
      const h = Math.floor(el.getBoundingClientRect().height);
      // 略小于容器，避免 ant-table 边框与滚动条导致溢出父级
      setTableScrollY(Math.max(48, h - 2));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [error]);

  const filteredRows = useMemo(() => {
    let rows = allRows;
    if (preset === 'expected-arrival-today') {
      if (arrivalCheckInFilter === 'checked-in') {
        rows = rows.filter((r) => roomStatusOfRow(r) === 1);
      } else if (arrivalCheckInFilter === 'not-checked-in') {
        rows = rows.filter((r) => roomStatusOfRow(r) === 0);
      }
    }
    if (preset === 'expected-departure-today') {
      if (departureCheckoutFilter === 'checked-out') {
        rows = rows.filter((r) => roomStatusOfRow(r) === 5);
      } else if (departureCheckoutFilter === 'not-checked-out') {
        rows = rows.filter((r) => roomStatusOfRow(r) !== 5);
      }
    }
    if (roomTypeFilter) {
      rows = rows.filter((r) => rowRoomType(r) === roomTypeFilter);
    }
    return rows;
  }, [
    allRows,
    preset,
    arrivalCheckInFilter,
    departureCheckoutFilter,
    roomTypeFilter,
  ]);

  const columns: ColumnsType<Record<string, unknown>> = [
    {
      title: '房号',
      dataIndex: 'room',
      key: 'room',
      width: 72,
      render: (_: unknown, r) => rowRoomNo(r),
    },
    {
      title: '客人',
      dataIndex: 'guest',
      key: 'guest',
      ellipsis: true,
      render: (_: unknown, r) => rowGuest(r),
    },
    {
      title: '房型',
      dataIndex: 'rt',
      key: 'rt',
      width: 120,
      ellipsis: true,
      render: (_: unknown, r) => rowRoomType(r),
    },
  ];

  const dataSource = filteredRows.map((r, i) => ({
    key: `${preset}-${storeId}-${arrivalCheckInFilter}-${departureCheckoutFilter}-${roomTypeFilter}-${i}`,
    ...r,
  }));

  const displayTitle = useMemo(() => {
    const n = filteredRows.length;
    if (preset === 'in-house-today') return `在住（${n}）`;
    if (preset === 'expected-arrival-today') return `预抵（${n}）`;
    if (preset === 'expected-departure-today') return `预离（${n}）`;
    return `${meta.title}（${n}）`;
  }, [preset, meta.title, filteredRows.length]);

  return (
    <section
      data-component-id={meta.dataId}
      aria-label={displayTitle}
      style={{
        width: '100%',
        minWidth: 280,
        height: 300,
        minHeight: 300,
        border: '1px solid #f0f0f0',
        borderRadius: 4,
        background: '#fff',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        ...style,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 12px',
          borderBottom: '1px solid #f0f0f0',
          flexShrink: 0,
          gap: 8,
        }}
      >
        <div style={{ minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 4 }}>
          {titleLeftAdornment && (
            <div
              style={{
                marginLeft: -4,
                marginRight: 4,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                paddingTop: 2,
              }}
            >
              {titleLeftAdornment}
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: '#333' }}>
              {displayTitle}
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
          }}
        >
          {preset === 'expected-arrival-today' ? (
            <Select<ArrivalCheckInFilter>
              size="small"
              aria-label="按是否已入住筛选预抵列表"
              style={{ width: 108 }}
              value={arrivalCheckInFilter}
              onChange={(v) => setArrivalCheckInFilter(v ?? ALL_ARRIVAL_CHECKIN)}
              options={[
                { label: '全部', value: ALL_ARRIVAL_CHECKIN },
                { label: '已入住', value: 'checked-in' },
                { label: '未入住', value: 'not-checked-in' },
              ]}
              disabled={loading || !!error}
            />
          ) : null}
          {preset === 'expected-departure-today' ? (
            <Select<DepartureCheckoutFilter>
              size="small"
              aria-label="按是否已退房筛选预离列表"
              style={{ width: 108 }}
              value={departureCheckoutFilter}
              onChange={(v) => setDepartureCheckoutFilter(v ?? ALL_DEPARTURE_CHECKOUT)}
              options={[
                { label: '全部', value: ALL_DEPARTURE_CHECKOUT },
                { label: '未退房', value: 'not-checked-out' },
                { label: '已退房', value: 'checked-out' },
              ]}
              disabled={loading || !!error}
            />
          ) : null}
          <Select
            size="small"
            placeholder="房型"
            allowClear
            aria-label="按房型筛选列表"
            style={{ width: 132 }}
            value={roomTypeFilter}
            onChange={(v) => setRoomTypeFilter(v ?? ALL_ROOM_TYPES)}
            options={[
              { label: '全部房型', value: ALL_ROOM_TYPES },
              ...roomTypeOptions.map((t) => ({ label: t, value: t })),
            ]}
            popupMatchSelectWidth={false}
            disabled={loading || !!error}
          />
          {loading && <Spin size="small" />}
        </div>
      </div>
      <div
        style={{
          padding: '8px 12px 12px',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {error && (
          <Typography.Text type="danger" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
            {formatOrderRoomPresetError(error)}
          </Typography.Text>
        )}
        <div
          ref={tableWrapRef}
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Table<Record<string, unknown>>
            size="small"
            showHeader={false}
            pagination={false}
            scroll={{ y: tableScrollY }}
            columns={columns}
            dataSource={dataSource}
            locale={{ emptyText: loading ? '加载中…' : '暂无数据' }}
          />
        </div>
      </div>
    </section>
  );
};

export default OrderRoomPresetCard;
